import React, { useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

// simple mic recorder for voice answers (MediaRecorder — no dependencies)
const VoiceRecorder = ({ onRecorded }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("idle"); // idle | recording | done | denied
  const [previewUrl, setPreviewUrl] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const start = () => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const recorder = new MediaRecorder(stream);
        recorderRef.current = recorder;
        chunksRef.current = [];
        recorder.ondataavailable = (event) => chunksRef.current.push(event.data);
        recorder.onstop = () => {
          stream.getTracks().forEach((track) => track.stop());
          const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
          const extension = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
          const file = new File([blob], `voice-answer.${extension}`, { type: blob.type });
          setPreviewUrl(URL.createObjectURL(blob));
          setPhase("done");
          onRecorded(file);
        };
        recorder.start();
        setPhase("recording");
      })
      .catch(() => setPhase("denied"));
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const reset = () => {
    setPreviewUrl(null);
    setPhase("idle");
    onRecorded(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "stretch" }}>
      {phase === "idle" && (
        <Button variant="outlined" onClick={start} sx={{ borderRadius: "12px", fontWeight: 700 }}>
          🎤 {t("wb_start_recording")}
        </Button>
      )}
      {phase === "recording" && (
        <Button variant="contained" color="error" onClick={stop} sx={{ borderRadius: "12px", fontWeight: 700, animation: "wbRec 1.2s infinite", "@keyframes wbRec": { "50%": { opacity: 0.6 } } }}>
          ⏹ {t("wb_stop_recording")}
        </Button>
      )}
      {phase === "done" && (
        <>
          <audio controls src={previewUrl} style={{ width: "100%" }} />
          <Button size="small" onClick={reset} sx={{ borderRadius: "12px" }}>
            🔄 {t("wb_rerecord")}
          </Button>
        </>
      )}
      {phase === "denied" && (
        <Typography fontSize="0.85rem" color="error">{t("wb_mic_denied")}</Typography>
      )}
    </Box>
  );
};

export default VoiceRecorder;
